import Image from "next/legacy/image";
import { BsDash, BsPlus, BsTrash } from "react-icons/bs";
import GiftOptions from "./newGiftOptions";
import { useAppContext } from "@context/app";
import { CartItemModel, CartStep } from "@types";
import { useTranslations } from "next-intl";
import Link from "next/link";
import CustomText from "./customText";
import { useRouter } from "next/router";
import { convertNumber, convertStringNumber } from "@utils/digits";
import { segmentEvent } from "@utils/segment";
import { CartProblemType } from "@templates/cartContainer";


interface Props {
  item: CartItemModel;
  changeQty: (id: string | number, count: number) => void;
  deleteItem: (id: string | number) => void;
  step?: "cart" | "gift";
  problem?: {
    id: string | number;
    type: CartProblemType;
    count?: number;
  };
  action?: (value: {
    next?: CartStep;
    current: CartStep;
    goCheckout?: boolean;
  }) => void;
}

const CartItem: React.FC<Props> = ({
  item,
  changeQty,
  deleteItem,
  step,
  problem,
  action,
}) => {


  const { currency, decimal, country, currency_iso, area, user, cart, freeShipping } =
    useAppContext();
  const t = useTranslations();
  const { locale } = useRouter()
  const ItemsQty = cart.reduce((previousValue, currentValue) => {
    return previousValue + currentValue.qty;
  }, 0);
  const totalPrice = cart.reduce((previousValue, currentValue) => {
    return previousValue + currentValue.qty * (currentValue.raw_price || currentValue.price);
  }, 0);

  const removeFromCart = (count: number) => {
    segmentEvent("SKU Removed", {
      action: count > 1 ? "Remove" : "Delete",
      product_id: item.productId,
      product_slug: item.productSlug,
      brand_id: item.brand_id,
      brand_slug: item.brand_slug,
      category_id: item.category_id,
      category_slug: item.category_slug,
      subcategory_id: item.subcategory_id,
      subcategory_slug: item.subcategory_slug,
      sku_id: item.id,
      sku_slug: item.skuSlug,
      currency: currency_iso.toUpperCase(),
      country_id: country?.id,
      country_slug: country?.slug,
      area_id: area?.id,
      area_slug: area?.slug,
      user_id: user.data?.id || -1,
      language: locale?.includes("en") ? "English" : "Arabic",
      sku_old_price: item.oldPrice || item.price,
      sku_new_price: item.price,
      from_page: "cart",
      from_entity: "",
      from_entity_slug: "",
      from_page_section: "",
      from_page_section_entity: "",
      from_page_section_slug: "",
      cart_count: ItemsQty ? (ItemsQty - 1) : 0,
      quantity: ItemsQty ? (ItemsQty - 1) : 0,
    });
  };

  const addToCartEvent = () => {
    segmentEvent("SKU Added", {
      product_id: item.productId,
      product_slug: item.productSlug,
      brand_id: item.brand_id,
      brand_slug: item.brand_slug,
      category_id: item.category_id,
      category_slug: item.category_slug,
      subcategory_id: item.subcategory_id,
      subcategory_slug: item.subcategory_slug,
      sku_id: item.id,
      sku_slug: item.skuSlug,
      currency: currency_iso.toUpperCase(),
      country_id: country?.id,
      country_slug: country?.slug,
      area_id: area?.id,
      area_slug: area?.slug,
      user_id: user.data?.id || -1,
      language: locale?.includes("en") ? "English" : "Arabic",
      sku_old_price: item.oldPrice || item.price,
      sku_new_price: item.price,
      price: item.price,
      from_page: "cart",
      from_entity: "",
      from_entity_slug: "",
      from_page_section: "",
      from_page_section_entity: "",
      from_page_section_slug: "",
      cart_count: ItemsQty + 1,
      quantity: ItemsQty + 1,
      subtotal_amount: totalPrice + (item.raw_price || item.price),
      free_shipping_threshold: freeShipping ?? undefined,
    });
  }
  const hasGift = item.gifts.length ? true : false
  const hasText = item.customText?.length ? true : false
  let _className = "";

  if (item.is_customizable) {
    if (hasGift || hasText) {
      _className = "w-full border-b border-[#eee] px-3";
    } else {
      _className = "w-1/2";
    }
  } else {
    _className = "w-full";
  }

  _className += " flex justify-center";


  let customTextComponent;
  if (item.is_customizable) {
    if (hasGift || hasText) {
      customTextComponent = null;
    } else {
      customTextComponent = <div className="w-[1px] h-11 bg-[#eee]"></div>;
    }
  } else {
    customTextComponent = null;
  }


  let customizableTextComponent;

  if (item.is_customizable) {
    if (hasGift || hasText) {
      customizableTextComponent = (
        <div className="w-full flex justify-center px-3">
          <CustomText item={{ ...item }} />
        </div>
      );
    } else {
      customizableTextComponent = (
        <div className="w-[calc(50%-1px)] flex justify-center">
          <CustomText item={{ ...item }} />
        </div>
      );
    }
  } else {
    customizableTextComponent = null;
  }

  return (
    <>
      <div className="w-full md:flex hidden flex-wrap items-center mb-5 md:justify-between justify-center">
        {!item.digital_gift_ids && item.product_source !== "onecard" ?

          <Link href={`/product/${item.productSlug ?? item.productId}?default_sku=${item.id}&source=${step === "cart" ? "cart" : "customizations"}`} prefetch={false}>

            <div className="relative w-40 h-40 rounded-xl overflow-hidden">
              <Image
                src={item.image}
                layout="fill"
                quality={100}
                alt=""
                objectFit="cover"
              ></Image>
            </div>

          </Link> :
          <div className="relative w-40 h-40 rounded-xl overflow-hidden">
            <Image
              src={item.image}
              layout="fill"
              quality={100}
              alt=""
              objectFit="cover"
            ></Image>
          </div>
        }

        <div className="p-2 flex flex-wrap items-center md:flex-1 sm:w-full w-full justify-between ">
          <div className="md:w-2/5 sm:w-full w-full flex flex-col h-36 justify-around">
            <div className="flex flex-col">
              <p className="font-normal text-lg text-[#333] leading-8 px-2 md:text-start text-center">
                {!item.digital_gift_ids && item.product_source !== "onecard" ?

                  <Link href={`/product/${item.productSlug ?? item.productId}?default_sku=${item.id}&source=${step === "cart" ? "cart" : "customizations"}`} prefetch={false}>

                    {`${convertStringNumber(locale, item.productName)}  (${convertStringNumber(locale, item.skuName)})`}

                  </Link> :
                  <>
                    {`${convertStringNumber(locale, item.productName)}  (${convertStringNumber(locale, item.skuName)})`}
                  </>
                }
              </p>
              {step === "cart" && item.assembly_price && (
                <p className="font-normal text-lg mt-2 text-[#555] mb-2 px-2 "> {t("assembly_service_included")} <span className="text-[#74411E]">{`${convertNumber(locale, item.assembly_price)} ${currency}`}</span></p>
              )}
            </div>
            {step === "gift" && (
              <p className="text-sm font-normal text-[#999]  px-2 md:text-start text-center">{`${convertNumber(locale, item.qty)
                } ${item.qty > 1 ? t("client_giftoptions_items") : t("client_giftoptions_item")}`}</p>
            )}
          </div>
          {step === "cart" && (
            <div
              className="md:w-1/5 sm:w-full w-full"
              dir="ltr"
            >
              <div className=" flex flex-wrap  w-[max-content] m-auto items-center justify-center">
                <button
                  className={`w-6 h-6 rounded-full border-[#008AF6] ${item.qty > 1 && !item.digital_gift_ids?.length && !item.registry_contribution_ids?.length ? "border-2" : ""
                    }  text-[#008AF6] text-xl hover:text-primary hover:border-primary flex items-center justify-center`}
                >
                  {item.qty > 1 && !item.digital_gift_ids?.length && !item.registry_contribution_ids?.length ? (
                    <BsDash
                      size={24}
                      onClick={() => {
                        removeFromCart(item.qty - 1);
                        changeQty(item.id, item.qty - 1);
                      }}
                    />
                  ) : (
                    <BsTrash
                      className=" cursor-pointer"
                      size={24}
                      onClick={() => {
                        removeFromCart(0);
                        deleteItem(item.id);
                      }}
                    />
                  )}
                </button>
                <p className="h-12 w-14 text-[#333] text-center flex justify-center items-center font-extrabold text-xl mx-2">
                  {convertNumber(locale, item.qty)}
                </p>
                {!item.digital_gift_ids?.length && !item.registry_contribution_ids?.length &&
                  <button
                    className={`w-6 h-6 rounded-full border-[#008AF6] border-2  text-[#008AF6] text-xl hover:text-primary hover:border-primary flex items-center justify-center disabled:text-gray-400 disabled:border-gray-400 disabled:cursor-not-allowed`}
                    onClick={() => {
                      addToCartEvent();
                      changeQty(item.id, item.qty + 1)
                    }}
                    disabled={item.buy_limit <= item.qty}
                  >
                    <BsPlus size={24} />
                  </button>
                }
              </div>
              {problem?.type === "less" ? (
                <p className=" font-normal text-sm w-full text-center text-[#7A3A00]">
                  {t("client_errors_onlyremaining").replace("@", `${problem?.count}`)}
                </p>
              ) : null}
            </div>
          )}
          {step === "cart" && (
            <div className="md:w-1/5 sm:w-full w-full">
              <p className="w-full font-extrabold text-lg text-[#333] md:text-center sm:text-center text-center left-to-right">
                <span
                  style={{ direction: "rtl" }}
                  className=" font-normal text-sm px-1 text-left"
                >
                  {currency}{" "}
                </span>{" "}
                {convertNumber(locale, item.price.toFixed(decimal))}
              </p>
            </div>
          )}
          {step === "gift" && !item.no_customizations && (
            <div className="  md:w-3/5 sm:w-full w-full flex flex-col md:items-end items-center md:mt-0 sm:mt-2 mt-2 ">
              <GiftOptions action={action} item={{ ...item }} />
              {item.is_customizable ? <CustomText item={{ ...item }} /> : null}
            </div>
          )}
        </div>
      </div>
      <div className="w-full md:hidden  mb-5 border border-[#eee] rounded-2xl ">
        <div className="w-full flex flex-wrap p-3  h-full">
          {!item.digital_gift_ids && item.product_source !== "onecard" ?

            <Link href={`/product/${item.productSlug ?? item.productId}?default_sku=${item.id}&source=${step === "cart" ? "cart" : "customizations"}`} prefetch={false}>

              <div className="relative w-24 h-24 rounded-xl overflow-hidden">
                <Image
                  src={item.image}
                  layout="fill"
                  quality={100}
                  alt=""
                  objectFit="cover"
                ></Image>
              </div>

            </Link>
            :
            <div className="relative w-24 h-24 rounded-xl overflow-hidden">
              <Image
                src={item.image}
                layout="fill"
                quality={100}
                alt=""
                objectFit="cover"
              ></Image>
            </div>

          }
          <div className=" flex-1">
            <div className="w-full h-[104px] p-1 px-3">
              {!item.digital_gift_ids && item.product_source !== "onecard" ?
                <Link href={`/product/${item.productSlug ?? item.productId}?default_sku=${item.id}&source=${step === "cart" ? "cart" : "customizations"}`} prefetch={false}>

                  <p className="font-normal text-sm text-textDark leading-5 mb-1 line-clamp-2" >
                    {`${convertStringNumber(locale, item.productName)}`}
                  </p>
                  <p className="font-normal text-xs text-textLight leading-5 mb-1 line-clamp-2">
                    {`${convertStringNumber(locale, item.skuName)}`}
                  </p>

                </Link>
                :
                <>
                  <p className="font-normal text-sm text-textDark leading-5 mb-1 line-clamp-2" >
                    {`${convertStringNumber(locale, item.productName)}`}
                  </p>
                  <p className="font-normal text-xs text-textLight leading-5 mb-1 line-clamp-2">
                    {`${convertStringNumber(locale, item.skuName)}`}
                  </p>
                </>
              }
              {step === "cart" && (
                <>
                  {item.assembly_price && (
                    <p className="font-normal text-sm text-[#333] mb-2"> {t("assembly_service_included")} <span className="text-[#74411E]">{`${convertNumber(locale, item.assembly_price)} ${currency}`}</span></p>
                  )}
                  <p className={`w-full font-normal text-xs text-[#999] flex space-x-1 ${locale?.includes("ar-") ? "justify-end" : ""}`} dir="ltr">
                    <span
                      dir="rtl"
                      className="text-left"
                    >
                      {currency}
                    </span>
                    <span>
                      {convertNumber(locale, item.price.toFixed(decimal))}
                    </span>
                  </p>
                </>
              )}
              {step === "gift" && (
                <p className="text-xs w-full font-normal text-[#999]">{`${convertNumber(locale, item.qty)
                  } ${item.qty > 1 ? t("client_giftoptions_items") : t("client_giftoptions_item")}`}</p>
              )}
            </div>

            {step === "cart" && (
              <div
                className="w-full"
              // dir="ltr"
              >
                <div className=" flex flex-wrap  w-[max-content] items-center mt-3">
                  <button
                    className={`w-6 h-6 rounded-full border-[#008AF6] ${item.qty > 1 && !item.digital_gift_ids?.length && !item.registry_contribution_ids?.length ? "border-2" : ""
                      }  text-[#008AF6] text-xl hover:text-primary hover:border-primary flex items-center justify-center`}
                  >
                    {item.qty > 1 && !item.digital_gift_ids?.length && !item.registry_contribution_ids?.length ? (
                      <BsDash
                        size={24}
                        onClick={() => {
                          removeFromCart(item.qty - 1);
                          changeQty(item.id, item.qty - 1);
                        }}
                      />
                    ) : (
                      <BsTrash
                        className=" cursor-pointer"
                        size={24}
                        onClick={() => {
                          removeFromCart(0);
                          deleteItem(item.id);
                        }}
                      />
                    )}
                  </button>
                  <p className=" w-12 text-[#333] text-center flex justify-center items-center font-extrabold text-xl mx-2">
                    {convertNumber(locale, item.qty)}
                  </p>
                  {!item.digital_gift_ids?.length && !item.registry_contribution_ids?.length &&
                    <button
                      className={`w-6 h-6 rounded-full border-[#008AF6] border-2  text-[#008AF6] text-xl hover:text-primary hover:border-primary flex items-center justify-center disabled:text-gray-400 disabled:border-gray-400 disabled:cursor-not-allowed`}
                      onClick={() => {
                        addToCartEvent();
                        changeQty(item.id, item.qty + 1)
                      }}
                      disabled={item.buy_limit <= item.qty}
                    >
                      <BsPlus size={24} />
                    </button>
                  }
                </div>
                {problem?.type === "less" ? (
                  <p className=" font-normal text-sm w-full text-center text-[#7A3A00]">
                    {t("client_errors_onlyremaining").replace("@", `${problem?.count}`)}
                  </p>
                ) : null}
              </div>
            )}
          </div>
        </div>
        {step === "gift" && !item.no_customizations && (
          <div className=" w-full flex flex-wrap border-t border-[#eee]">
            <div className={`${_className}`}>
              <GiftOptions action={action} item={{ ...item }} />
            </div>

            {customTextComponent}
            {customizableTextComponent}
          </div>
        )}
      </div>
    </>
  );
};

export default CartItem;
